import { cn } from "@-/com.npmjs.cn";
import { ActivityIndicator, Pressable, View } from "@-/com.npmjs.react-native";
import type { SharedValue } from "@-/com.npmjs.react-native-reanimated";
import { useContextSelectors } from "@-/com.npmjs.use-context-selectors";
import type { ComponentProps } from "react";
import { Text } from "@-/dev.improve.haniverse.components.ui.text";
import { Icon, type IconName } from "@-/dev.improve.haniverse.components.ui.icon";
import {
  GlassContentScope,
  ToolbarGlassSurface,
  useToolbarGridMetrics,
} from "@-/dev.improve.haniverse.components.ui.toolbar.button.view";
import {
  learnCardActionStatusLabel,
  type LearnCardAction,
} from "@-/dev.improve.haniverse.lib.learn.card-action";
import { LearnDeckContext } from "@-/dev.improve.haniverse.routes.nav.app.learn.deck.context";
import {
  LearnFlashcardButtonPressDetector,
  LearnFlashcardSwipePressHighlight,
  useLearnCardButtonPress,
} from "@-/dev.improve.haniverse.routes.nav.app.learn._lib.card-gestures";

/**
 * The button's press, shaded over it without moving the stationary footer or dimming its white
 * base: a finger on the button, or a card dragged toward its action.
 */
function LearnCardActionPressHighlight({
  className,
  direction,
  pressed,
  testID,
}: {
  className?: string;
  direction: -1 | 1 | "both" | undefined;
  pressed: SharedValue<number>;
  testID?: string;
}) {
  const [stage] = useContextSelectors(LearnDeckContext, (c) => c.stage);
  return (
    <LearnFlashcardSwipePressHighlight
      className={className}
      direction={direction}
      pressed={pressed}
      stage={stage}
      testID={testID}
    />
  );
}

/**
 * Keep the action mounted through saving, successor loading, exit and retry. A failed save returns
 * the card to rest, so every button is simply available again. A save whose outcome is unknown, or
 * a failed load, resets the owning button to its normal look (the deck toasts the error); pressing
 * it again retries that same operation.
 */
export function LearnFlashcardActionButton({
  action,
  className,
  compact = false,
  detail,
  detailAccessibilityLabel,
  disabled = false,
  handlesAllActions = false,
  icon = "LuChevronRight",
  label,
  onPress,
  onPressIn,
  pill = false,
  selected = false,
  swipeDirection,
  testID,
}: {
  action: LearnCardAction;
  className?: string;
  compact?: boolean;
  detail?: string;
  /** Spoken form of `detail`, so an abbreviated interval stays purely visual. */
  detailAccessibilityLabel?: string;
  disabled?: boolean;
  /** A single lesson/slide button also owns progress for menu and swipe actions. */
  handlesAllActions?: boolean;
  icon?: IconName;
  label: string;
  onPress?: ComponentProps<typeof Pressable>["onPress"];
  onPressIn?: ComponentProps<typeof Pressable>["onPressIn"];
  /** Round a `compact` button's corners fully — the squeezed keyboard-visible rating row. */
  pill?: boolean;
  selected?: boolean;
  swipeDirection?: -1 | 1 | "both";
  testID: string;
}) {
  const { isLandscape } = useToolbarGridMetrics();
  const [transition, isTransitioning, commit, recover, retryDestination, isExploring] =
    useContextSelectors(
      LearnDeckContext,
      (c) => c.state.transition,
      (c) => c.isTransitioning,
      (c) => c.commit,
      (c) => c.recover,
      (c) => c.retryDestination,
      (c) => c.exploration !== null,
    );
  const active =
    transition.phase === "committed" || transition.phase === "exiting" ? transition : null;
  const ownsAction =
    active !== null &&
    (handlesAllActions ||
      (active.action.kind === action.kind &&
        (action.kind !== "rate" ||
          (active.action.kind === "rate" && active.action.rating === action.rating))));
  const writeUnknown =
    ownsAction && transition.phase === "committed" && transition.writeStatus === "unknown";
  const nextFailed =
    ownsAction &&
    transition.phase === "committed" &&
    transition.writeStatus === "saved" &&
    transition.destinationStatus === "failed";
  const hasError = writeUnknown || nextFailed;
  const isPending = ownsAction && transition.phase === "committed" && !hasError;
  const isDisabled = !hasError && (disabled || isTransitioning || isPending);
  const isSaving = transition.phase === "committed" && transition.writeStatus === "pending";
  const statusLabel =
    ownsAction && active !== null
      ? learnCardActionStatusLabel(active.action, isSaving ? "persisting" : "loading-next")
      : label;
  const shapeClassName =
    compact && !isLandscape ? (pill ? "rounded-full" : "rounded-xs") : "rounded-full";
  const isDifficulty = action.kind === "rate";
  const press = useLearnCardButtonPress(!isDisabled);

  return (
    <LearnFlashcardButtonPressDetector press={press}>
      <Pressable
        accessibilityLabel={
          isPending
            ? statusLabel
            : detail
              ? `${label}, next review in ${detailAccessibilityLabel ?? detail}`
              : label
        }
        accessibilityRole="button"
        accessibilityState={{ busy: isPending, disabled: isDisabled }}
        className={cn(
          "relative size-full items-center justify-center overflow-hidden py-1 shadow-none",
          shapeClassName,
          compact && "flex-col gap-0",
          compact && (pill ? "px-1" : "px-2"),
          !compact && "px-2",
          isDifficulty && "border border-black/15 bg-white",
          isDifficulty && (isDisabled ? "border-black/10" : "hover:bg-gray-100"),
          isDisabled && "cursor-not-allowed",
          isPending && "cursor-wait",
          selected && "border-2 border-black/15",
          selected && !isDisabled && "border-black",
          className,
        )}
        disabled={isDisabled}
        onPress={
          writeUnknown
            ? recover
            : nextFailed
              ? retryDestination
              : (onPress ?? (() => commit(action, "button")))
        }
        onPressIn={(event) => {
          press.handlers.onPressIn();
          onPressIn?.(event);
        }}
        onPressOut={press.handlers.onPressOut}
        testID={testID}
      >
        {/* The label sits beside the glass, not in it, so both share one scope. */}
        <GlassContentScope>
          {!isDifficulty ? (
            <ToolbarGlassSurface
              className="absolute inset-0"
              interactive={false}
              pointerEvents="none"
            />
          ) : null}
          {/* Exploring, a swipe presses the card's Back to Review pill rather than this action. */}
          {isDisabled ? null : (
            <LearnCardActionPressHighlight
              className={shapeClassName}
              direction={isExploring ? undefined : swipeDirection}
              pressed={press.pressed}
              testID={`${testID}-press-highlight`}
            />
          )}
          <View
            className={cn("min-w-0 shrink items-center justify-center", isPending && "opacity-25")}
          >
            {isLandscape ? (
              <Icon
                className={
                  isDisabled && !isPending
                    ? "text-neutral-400"
                    : isDifficulty
                      ? "text-black"
                      : "text-foreground"
                }
                name={icon}
                size={isDifficulty ? 28 : 22}
              />
            ) : (
              <Text
                className={cn(
                  "shrink text-center",
                  isDisabled && !isPending
                    ? "text-neutral-400"
                    : isDifficulty
                      ? "text-black"
                      : "text-foreground",
                  compact && "text-xs leading-3.5",
                  selected && "font-semibold",
                )}
              >
                {label}
              </Text>
            )}
            {compact && detail ? (
              <Text
                className={cn(
                  "text-[10px] leading-3",
                  isDisabled && !isPending ? "text-neutral-400" : "text-neutral-600",
                )}
              >
                {detail}
              </Text>
            ) : null}
          </View>
          {isPending ? (
            <View className="absolute inset-0 items-center justify-center" pointerEvents="none">
              <ActivityIndicator color="#737373" size="small" testID={`${testID}-pending`} />
            </View>
          ) : null}
        </GlassContentScope>
      </Pressable>
    </LearnFlashcardButtonPressDetector>
  );
}
